import type {
  PromptTemplateItem,
  UpsertPromptTemplateRequest,
} from '@glyphic/user-api-types'

import { resolveWirePublicId } from '../lib/publicId'
import { apiRequest } from './client'

export type PromptTemplate = {
  id: string
  name: string
  style: string
  body: string
  scope: string
  editable: boolean
}

function toClient(item: PromptTemplateItem & { id?: string; public_id?: string }): PromptTemplate {
  return {
    id: resolveWirePublicId(item, 'publicId', 'public_id', 'id'),
    name: item.name,
    style: item.style,
    body: item.body,
    scope: item.scope,
    editable: item.editable,
  }
}

export async function fetchPromptTemplates(): Promise<PromptTemplate[]> {
  const rows = await apiRequest<PromptTemplateItem[]>('/v1/prompt-templates')
  return rows.map(toClient)
}

export async function createPromptTemplate(
  entry: Omit<PromptTemplate, 'id' | 'scope' | 'editable'>,
): Promise<PromptTemplate> {
  const body: UpsertPromptTemplateRequest = {
    name: entry.name.trim(),
    style: entry.style.trim() || undefined,
    body: entry.body.trim(),
    sortOrder: 0,
  }
  const row = await apiRequest<PromptTemplateItem>('/v1/prompt-templates', {
    method: 'POST',
    body: JSON.stringify(body),
  })
  return toClient(row)
}

export async function updatePromptTemplate(
  id: string,
  patch: Partial<Pick<PromptTemplate, 'name' | 'style' | 'body'>>,
): Promise<boolean> {
  const body: UpsertPromptTemplateRequest = {
    name: (patch.name ?? '').trim(),
    style: patch.style?.trim() || undefined,
    body: (patch.body ?? '').trim(),
    sortOrder: 0,
  }
  await apiRequest<PromptTemplateItem>(`/v1/prompt-templates/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
  return true
}

export async function removePromptTemplate(id: string): Promise<boolean> {
  await apiRequest<void>(`/v1/prompt-templates/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  return true
}
